import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSession, sendMessage, sendProctoringEvent, endSession } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Briefcase, Send, Camera, CameraOff, Eye, AlertTriangle, Clock, Loader2, StopCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useProctorController } from "@/hooks/useProctorController";
import BrandedHeader from "@/components/BrandedHeader";

const STAGES = [
  { id: 0, name: "Calibration", weight: "0%" },
  { id: 1, name: "Foundational", weight: "20%" },
  { id: 2, name: "Depth Probing", weight: "30%" },
  { id: 3, name: "Scenario", weight: "35%" },
  { id: 4, name: "Closing", weight: "15%" },
];

const DIFFICULTY_NAMES = ["Recall", "Application", "Analysis", "Synthesis", "Expert"];

function stageRowClass(stageId, currentStage) {
  if (stageId === currentStage) return 'bg-primary/10 border border-primary/20';
  if (stageId < currentStage) return 'opacity-60';
  return 'opacity-40';
}

function stageBadgeClass(stageId, currentStage) {
  if (stageId === currentStage) return 'bg-primary text-primary-foreground';
  if (stageId < currentStage) return 'bg-[hsl(var(--success))]/20 text-[hsl(var(--success))]';
  return 'bg-secondary text-muted-foreground';
}

function tabSwitchBadgeClass(count) {
  if (count === 0) return 'text-[hsl(var(--success))]';
  if (count <= 2) return 'text-[hsl(var(--warning))]';
  return 'text-[hsl(var(--destructive))]';
}

function proctorAlertText({ cameraError, faceDetected }) {
  if (cameraError) return 'Camera access required for proctoring';
  if (!faceDetected) return 'Please ensure your face is visible';
  return 'Multiple integrity violations detected';
}

function eventSeverityDotClass(evt) {
  if (evt.event_type.includes('TAB')) return 'bg-[hsl(var(--warning))]';
  if (evt.severity === 'high') return 'bg-[hsl(var(--destructive))]';
  return 'bg-[hsl(var(--warning))]';
}

export default function CandidateInterviewPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const [interviewState, setInterviewState] = useState({ current_stage: 0, current_difficulty_level: 1, questions_asked: 0 });
  const [elapsedTime, setElapsedTime] = useState(0);
  const [proctorCollapsed, setProctorCollapsed] = useState(false);
  
  const chatEndRef = useRef(null);
  const scrollRef = useRef(null);
  const timerRef = useRef(null);

  // Use shared proctor controller
  const proctor = useProctorController({
    enabled: true,
    onAnomalyDetected: (anomaly) => {
      // Send to backend
      sendProctoringEvent(sessionId, {
        event_type: anomaly.event_type,
        details: anomaly.details,
        count: 1,
      }).catch(() => {});
    },
  });

  // Load session on mount
  useEffect(() => {
    loadSession();
    startTimer();
    
    return () => {
      clearInterval(timerRef.current);
    };
  }, [sessionId]); // Dependencies: sessionId (loadSession and startTimer are stable functions defined below)

  // Auto-scroll on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadSession = useCallback(async () => {
    try {
      const res = await getSession(sessionId);
      setSession(res.data);
      setMessages(res.data.messages || []);
      setInterviewState(res.data.interview_state || { current_stage: 0, current_difficulty_level: 1 });
    } catch (e) {
      toast.error("Failed to load session");
    }
  }, [sessionId]);

  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
  }, []);

  const handleSend = async () => {
    if (!inputText.trim() || sending) return;
    
    const userMsg = { role: "user", content: inputText.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    const msgText = inputText.trim();
    setInputText("");
    setSending(true);
    
    try {
      const res = await sendMessage(sessionId, {
        message: msgText,
        pending_proctoring_events: proctor.anomalyLogs.slice(-10).map(log => ({
          event_type: log.event_type,
          details: log.details,
        })),
      });
      
      const aiMsg = { role: "assistant", content: res.data.ai_message, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, aiMsg]);
      
      if (res.data.interview_state) {
        setInterviewState(res.data.interview_state);
      }
    } catch (e) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleEndInterview = async () => {
    if (ending) return;
    setEnding(true);
    try {
      await endSession(sessionId);
      navigate(`/interview/${sessionId}/complete`);
    } catch (e) {
      toast.error("Failed to end interview");
      setEnding(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const currentStage = interviewState?.current_stage ?? 0;
  const currentDifficulty = interviewState?.current_difficulty_level ?? 1;
  const questionsAsked = interviewState?.questions_asked ?? 0;

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      {/* Top Bar */}
      <BrandedHeader className="flex-shrink-0">
        <div className="flex items-center gap-3 flex-1">
          <Briefcase className="w-5 h-5 text-primary" />
          <span className="font-display font-semibold text-sm sm:text-base">AI Interview</span>
          <Badge variant="secondary" className="text-xs font-mono" data-testid="interview-timer">
            <Clock className="w-3 h-3 mr-1" />
            {formatTime(elapsedTime)}
          </Badge>
          <div className="flex items-center gap-2 ml-auto">
            <Badge variant="outline" className="text-xs">
              Q: {questionsAsked} | L{currentDifficulty} {DIFFICULTY_NAMES[currentDifficulty - 1] || ''}
            </Badge>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleEndInterview}
              disabled={ending}
              className="btn-press"
              data-testid="interview-end-button"
            >
              {ending ? <Loader2 className="w-3 h-3 animate-spin" /> : <StopCircle className="w-3 h-3 mr-1" />}
              End
            </Button>
          </div>
        </div>
      </BrandedHeader>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Stage Stepper */}
        <div className="hidden lg:flex w-[240px] flex-shrink-0 border-r border-border flex-col py-4 px-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4 px-2">Interview Stages</h3>
          <div className="space-y-1" data-testid="interview-stage-stepper">
            {STAGES.map(stage => (
              <div
                key={stage.id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${stageRowClass(stage.id, currentStage)}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${stageBadgeClass(stage.id, currentStage)}`}>
                  {stage.id < currentStage ? '\u2713' : stage.id}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-tight">{stage.name}</p>
                  <p className="text-xs text-muted-foreground">{stage.weight}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Progress */}
          <div className="mt-auto px-2">
            <p className="text-xs text-muted-foreground mb-2">Progress</p>
            <Progress value={(currentStage / 4) * 100} className="h-1.5" />
            <p className="text-xs text-muted-foreground mt-1 font-mono">{Math.round((currentStage / 4) * 100)}%</p>
          </div>
        </div>

        {/* Center: Chat */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile stage indicator */}
          <div className="lg:hidden flex items-center gap-2 px-4 py-2 border-b border-border overflow-x-auto">
            {STAGES.map(stage => (
              <Badge
                key={stage.id}
                variant={stage.id === currentStage ? "default" : "secondary"}
                className={`text-xs whitespace-nowrap flex-shrink-0 ${stage.id < currentStage ? 'opacity-60' : ''}`}
              >
                {stage.name}
              </Badge>
            ))}
          </div>

          {/* Chat Messages */}
          <ScrollArea className="flex-1 px-4 py-4" ref={scrollRef} data-testid="interview-chat-scroll-area">
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((msg) => (
                <div key={msg.timestamp || `msg-${msg.content?.substring(0, 30)}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} message-enter`}>
                  <div className={`max-w-[42rem] rounded-xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-card border border-primary/20 text-foreground'
                      : 'bg-secondary border border-border text-foreground'
                  }`}>
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                    <div className="text-xs text-muted-foreground mt-2 font-mono">
                      {msg.role === 'user' ? 'You' : 'InterviewIQ'}
                      {msg.timestamp && ` \u00B7 ${new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                    </div>
                  </div>
                </div>
              ))}
              
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-secondary border border-border rounded-xl px-4 py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-muted-foreground typing-dot" />
                      <div className="w-2 h-2 rounded-full bg-muted-foreground typing-dot" />
                      <div className="w-2 h-2 rounded-full bg-muted-foreground typing-dot" />
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </ScrollArea>

          {/* Composer */}
          <div className="border-t border-border bg-background/80 backdrop-blur px-4 py-3 flex-shrink-0" data-testid="interview-chat-composer">
            {/* Camera blocking message */}
            {!proctor.cameraActive && (
              <div className="max-w-3xl mx-auto mb-3 bg-[hsl(var(--destructive))]/10 border border-[hsl(var(--destructive))]/30 rounded-lg p-4 text-center">
                <Camera className="w-8 h-8 text-[hsl(var(--destructive))] mx-auto mb-2" />
                <p className="text-sm font-medium text-[hsl(var(--destructive))] mb-1">Camera Required to Continue</p>
                <p className="text-xs text-muted-foreground">Please enable your camera to send messages in this interview.</p>
              </div>
            )}
            
            <div className="max-w-3xl mx-auto flex gap-2">
              <textarea
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Type your response..."
                rows={1}
                className="flex-1 min-h-[44px] max-h-[160px] resize-none rounded-lg border border-input bg-card px-3 py-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                data-testid="interview-chat-composer-textarea"
                disabled={sending || ending || !proctor.cameraActive}
              />
              <Button
                onClick={handleSend}
                disabled={!inputText.trim() || sending || !proctor.cameraActive}
                size="icon"
                className="h-[44px] w-[44px] btn-press flex-shrink-0"
                data-testid="interview-chat-send-button"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Right: Proctor Widget */}
        <div
          className={`hidden lg:flex flex-col border-l border-border transition-all duration-300 ${
            proctorCollapsed ? 'w-[50px]' : 'w-[300px]'
          }`}
          data-testid="proctor-widget"
        >
          <div className="flex items-center justify-between px-3 py-3 border-b border-border">
            {!proctorCollapsed && <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Proctoring</h3>}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setProctorCollapsed(!proctorCollapsed)}>
              {proctorCollapsed ? <ChevronLeft className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </Button>
          </div>
          
          {!proctorCollapsed && (
            <div className="p-3 space-y-3">
              {/* Camera View */}
              <div className="aspect-video rounded-lg bg-black/40 overflow-hidden relative">
                {proctor.cameraActive ? (
                  <video ref={proctor.videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <CameraOff className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
                {/* Status overlay */}
                <div className="absolute top-2 right-2">
                  <div className={`w-2.5 h-2.5 rounded-full relative ${
                    proctor.cameraActive && proctor.faceDetected ? 'bg-[hsl(var(--success))]' : 'bg-[hsl(var(--destructive))]'
                  }`} data-testid="proctor-status-indicator">
                    {proctor.cameraActive && proctor.faceDetected && <div className="absolute inset-0 rounded-full bg-[hsl(var(--success))] animate-ping opacity-75" />}
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Camera className="w-3 h-3" /> Camera
                  </span>
                  <Badge variant="secondary" className={`text-xs ${proctor.cameraActive ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--destructive))]'}`}>
                    {proctor.cameraActive ? 'Active' : 'Off'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Eye className="w-3 h-3" /> Face
                  </span>
                  <Badge variant="secondary" className={`text-xs ${proctor.faceDetected ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--warning))]'}`}>
                    {proctor.faceDetected ? 'Visible' : 'Not Visible'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <AlertTriangle className="w-3 h-3" /> Tab Switches
                  </span>
                  <Badge variant="secondary" className={`text-xs ${tabSwitchBadgeClass(proctor.tabSwitchCount)}`}>
                    {proctor.tabSwitchCount}
                  </Badge>
                </div>
              </div>

              {/* Proctor Alert Banner */}
              {(proctor.tabSwitchCount >= 2 || !proctor.faceDetected || proctor.cameraError) && (
                <div className="bg-[hsl(var(--destructive))]/10 border border-[hsl(var(--destructive))]/25 rounded-lg p-2.5" data-testid="proctor-alert-banner">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-[hsl(var(--destructive))] mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-[hsl(var(--destructive))]">
                      {proctorAlertText({ cameraError: proctor.cameraError, faceDetected: proctor.faceDetected })}
                    </p>
                  </div>
                </div>
              )}

              {/* Recent Events */}
              {proctor.anomalyLogs.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground font-medium">Recent Events</p>
                  {proctor.anomalyLogs.slice(-3).map((evt, i) => (
                    <div key={`${evt.timestamp || ""}-${evt.event_type}-${i}`} className="text-xs text-muted-foreground flex items-center gap-1.5 py-1">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${eventSeverityDotClass(evt)}`} />
                      <span className="truncate">{evt.event_type.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
